import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Row = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let alive = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user || !alive) return;
      setUserId(data.user.id);
      const { data: notes } = await supabase
        .from("notifications")
        .select("id, kind, title, body, link, read_at, created_at")
        .order("created_at", { ascending: false })
        .limit(30);
      if (!alive) return;
      setRows((notes ?? []) as Row[]);

      const channelName = `notif:${data.user.id}:${Math.random().toString(36).slice(2, 9)}`;
      channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${data.user.id}` },
          (payload) => setRows((prev) => [payload.new as Row, ...prev].slice(0, 30)),
        )
        .subscribe();
    })();
    return () => {
      alive = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const unread = rows.filter((r) => !r.read_at).length;

  async function markAllRead() {
    if (!userId) return;
    const ids = rows.filter((r) => !r.read_at).map((r) => r.id);
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    await supabase.from("notifications").update({ read_at: now }).in("id", ids);
    setRows((prev) => prev.map((r) => (r.read_at ? r : { ...r, read_at: now })));
  }

  if (!userId) return null;

  return (
    <div className="relative">
      <button
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border hairline transition hover:border-gold"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-gold px-1 text-[10px] font-medium leading-[18px] text-black">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-80 rounded-lg border hairline bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b hairline px-4 py-3">
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Notifications</p>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-gold hover:underline">Mark all read</button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {rows.length === 0 ? (
                <p className="p-6 text-center text-xs text-muted-foreground">You're all caught up.</p>
              ) : (
                <ul>
                  {rows.map((r) => (
                    <li key={r.id} className={`border-b hairline last:border-0 ${r.read_at ? "" : "bg-gold/[0.03]"}`}>
                      <a
                        href={r.link ?? "#"}
                        onClick={(e) => { if (!r.link) e.preventDefault(); }}
                        className="block px-4 py-3 transition hover:bg-white/[0.02]"
                      >
                        <p className="text-sm font-medium">{r.title}</p>
                        {r.body && <p className="mt-1 text-xs text-muted-foreground">{r.body}</p>}
                        <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                          {new Date(r.created_at).toLocaleString()}
                        </p>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
